// What the timeblocker part keeps doing in the desktop app, drawing nothing:
// handing the running Block (else the next one) to the menu bar, and doing
// what its buttons ask with the screen's own mutations. The menu bar never
// fetches; it shows the value the screen already holds (`useNowBlock`), sent
// only when it changed, and counts the elapsed time with its own clock.
import type {
  TimeBlock,
  TimeBlockerNow,
} from "@trameria/core/timeblocker-shared";
import { m } from "@trameria/i18n";
import { useEffect, useRef } from "react";
import { todayIn } from "../cases/dates";
import { apiErrorMessage } from "../data/errors";
import {
  type DesktopMenuBarBlock,
  type DesktopMenuBarBlockAction,
  type DesktopShell,
  desktopShell,
} from "../device/desktop-shell";
import { useTimeZone } from "../me/profile";
import { toast } from "../ui/toast";
import { blockLabel } from "./day";
import { useStartEndBlock, useUpdateAction } from "./queries";
import { useNowBlock } from "./running";
import { workingLinks } from "./works";

/** The menu bar's Block: the running one, else the next one, else none. */
export function menuBarBlockOf(
  now: TimeBlockerNow,
): DesktopMenuBarBlock | null {
  const block = now.running ?? now.next;
  if (!block) return null;
  const working = now.running ? workingLinks(block, Date.now()) : [];
  return {
    id: block.id,
    title: blockLabel(block),
    state: now.running ? "running" : "next",
    startAt: block.startAt,
    endAt: block.endAt,
    startedAt: block.startedAt,
    // A dropped Action is no longer part of the work.
    items: block.links
      .filter((link) => link.status !== "dropped")
      .map((link) => ({
        caseId: link.caseId,
        stepId: link.stepId,
        title: link.stepTitle ?? link.caseTitle,
        caseTitle: link.caseTitle,
        done: link.status === "done",
        working: working.includes(link),
      })),
  };
}

/** Mounted once as the timeblocker part's Background; nothing outside the desktop app. */
export function TimeBlockerMenuBar() {
  const shell = desktopShell();
  return shell?.setMenuBarBlock ? <MenuBarBlockFeed shell={shell} /> : null;
}

function MenuBarBlockFeed({ shell }: { shell: DesktopShell }) {
  const now = useNowBlock();
  const timeZone = useTimeZone();
  const startEnd = useStartEndBlock();
  const updateAction = useUpdateAction();
  const value = menuBarBlockOf(now);
  const json = JSON.stringify(value);
  const sent = useRef<string | null>(null);

  // frontend-development: a write to the desktop app (its menu bar), only when the value changed; nothing is fetched.
  useEffect(() => {
    if (sent.current === json) return;
    sent.current = json;
    shell.setMenuBarBlock?.(JSON.parse(json) as DesktopMenuBarBlock | null);
  }, [json, shell]);

  // frontend-development: the menu bar is cleared when the screen that feeds it goes away (sign-out).
  useEffect(
    () => () => {
      shell.setMenuBarBlock?.(null);
      sent.current = null;
    },
    [shell],
  );

  // The click handler reads the latest values without subscribing again.
  const latest = useRef({ now, timeZone, startEnd, updateAction });
  useEffect(() => {
    latest.current = { now, timeZone, startEnd, updateAction };
  });

  // frontend-development: clicks from the desktop app's menu bar (not a server event); each calls the screen's own mutation.
  useEffect(() => {
    const failed = (e: Error) =>
      toast(apiErrorMessage(e, m.timeblocker_save_failed()));
    return shell.onMenuBarBlockAction?.((action: DesktopMenuBarBlockAction) => {
      const { now, timeZone, startEnd, updateAction } = latest.current;
      const day = todayIn(timeZone);
      if (action.type === "block-step-done") {
        updateAction.mutate(
          {
            day,
            timeZone,
            action: { id: action.stepId, caseId: action.caseId },
            patch: { status: "done" },
          },
          { onError: failed },
        );
        return;
      }
      const end = action.type === "block-end";
      // Only the Block the menu bar shows, in the state it shows it.
      const block: TimeBlock | null = end ? now.running : now.next;
      if (block?.id !== action.id) return;
      startEnd.mutate({ day, timeZone, block, end }, { onError: failed });
    });
  }, [shell]);

  return null;
}
